import React, { useState, useEffect, useRef } from 'react'
import {
  Grid,
  TextField,
  Typography,
  Container,
  Box,
  Alert,
  Fade,
} from '@mui/material'
import { Refresh } from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { verifyLoginOTP, resendLoginOTP, clearMessageError, resendOTPFailure } from '../../redux/actions/authAction'
import CustomButton from '../../components/CustomButton'
import { isMobile } from '../../utils/utils'

const GOLD = '#c9a96e'

const fadeInUpKeyframes = {
  '@keyframes fadeInUp': {
    from: { opacity: 0, transform: 'translateY(24px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}

const stagger = (index) => ({
  opacity: 0,
  animation: 'fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards',
  animationDelay: `${index * 0.1}s`,
})

const OTPVerificationScreen = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { loading, error, message, otpRequested, pendingEmail } = useSelector(state => state.auth)
  const queryParams = new URLSearchParams(location.search)
  const emailFromQuery = queryParams.get('email')
  const email = pendingEmail || emailFromQuery || ''

  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isResendDisabled, setIsResendDisabled] = useState(true)
  const [countdown, setCountdown] = useState(60)
  const [isVerifying, setIsVerifying] = useState(false)
  const inputRefs = useRef([])
  const isMobileView = isMobile()

  useEffect(() => {
    if (!email) {
      navigate('/login')
    }

    const focusTimer = setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
        if (isMobileView) {
          inputRefs.current[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }, 100)

    dispatch(clearMessageError())

    setIsResendDisabled(true)
    setCountdown(60)

    const timer = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(timer)
          setIsResendDisabled(false)
          return 0
        }
        return prevCount - 1
      })
    }, 1000)

    return () => {
      clearInterval(timer)
      clearTimeout(focusTimer)
    }
  }, [dispatch, email, navigate, isMobileView])

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
  }

  const handleOtpChange = (index, value) => {
    if (/^[0-9]*$/.test(value)) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)

      if (value && index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus()
      }

      if (newOtp.every(digit => digit) && newOtp.join('').length === 6) {
        handleVerifyOtp(newOtp.join(''))
      }
    }
  }

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    if (/^[0-9]{6}$/.test(pastedData)) {
      const otpArray = pastedData.split('')
      setOtp(otpArray)
      if (inputRefs.current[5]) {
        inputRefs.current[5].focus()
      }
      handleVerifyOtp(pastedData)
    }
  }

  const handleVerifyOtp = async (otpValue = otp.join('')) => {
    if (otpValue.length !== 6) return

    setIsVerifying(true)
    try {
      await dispatch(verifyLoginOTP(email, otpValue, navigate))
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResendOtp = async () => {
    try {
      setIsResendDisabled(true)
      setCountdown(60)
      await dispatch(resendLoginOTP(email))

      const timer = setInterval(() => {
        setCountdown(prevCount => {
          if (prevCount <= 1) {
            clearInterval(timer)
            setIsResendDisabled(false)
            return 0
          }
          return prevCount - 1
        })
      }, 1000)
    } catch (err) {
      let errorMessage = "Une erreur s'est produite lors du renvoi de l'OTP."

      if (err.status === 404) {
        errorMessage = "Utilisateur non trouvé."
      } else if (err.status === 429) {
        errorMessage = "Trop de tentatives. Veuillez réessayer plus tard."
      }

      dispatch(resendOTPFailure(errorMessage))
      setIsResendDisabled(false)
      setCountdown(0)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  const otpInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      color: '#fff',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        transition: 'border-color 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.16)',
      },
      '&.Mui-focused fieldset': {
        borderColor: GOLD,
        borderWidth: '1.5px',
      },
    },
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#000',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        ...fadeInUpKeyframes,
      }}
    >
      {/* Grain texture overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.025,
          pointerEvents: 'none',
          zIndex: 1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '256px 256px',
        }}
      />

      <Grid container sx={{ minHeight: '100vh', position: 'relative', zIndex: 2 }}>
        {/* Left Column — Branding */}
        {!isMobileView && (
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: '#000',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              padding: 6,
            }}
          >
            {/* Radial gold glow */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(ellipse 70% 50% at 50% 40%, rgba(201, 169, 110, 0.05) 0%, transparent 70%)`,
                pointerEvents: 'none',
              }}
            />

            {/* Subtle grid overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.025,
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                backgroundSize: '80px 80px',
                pointerEvents: 'none',
              }}
            />

            {/* Logo */}
            <Box sx={{ ...stagger(0), mb: 5 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid rgba(255,255,255,0.06)',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/logo/logo-circle-dark-mode.svg"
                  alt="Lawis Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }}
                />
              </Box>
            </Box>

            {/* Heading */}
            <Typography
              sx={{
                ...stagger(1),
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 300,
                fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
                color: '#fff',
                textAlign: 'center',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                mb: 2.5,
                zIndex: 2,
                position: 'relative',
              }}
            >
              Vérification sécurisée
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                ...stagger(2),
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 400,
                fontSize: '15px',
                color: 'rgba(255, 255, 255, 0.40)',
                textAlign: 'center',
                maxWidth: '380px',
                lineHeight: 1.7,
                zIndex: 2,
                position: 'relative',
              }}
            >
              Confirmez votre identité avec le code envoyé à votre adresse email.
            </Typography>

            {/* Decorative gold line */}
            <Box
              sx={{
                ...stagger(3),
                width: '48px',
                height: '1.5px',
                backgroundColor: GOLD,
                opacity: 0.4,
                mt: 4,
                borderRadius: '1px',
              }}
            />
          </Grid>
        )}

        {/* Right Column — OTP Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            padding: isMobileView ? 3 : 6,
            backgroundColor: '#0d0d0d',
            borderLeft: isMobileView ? 'none' : '1px solid rgba(255, 255, 255, 0.04)',
            position: 'relative',
          }}
        >
          {/* Mobile logo */}
          {isMobileView && (
            <Box sx={{ ...stagger(0), mb: 4, mt: 4 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid rgba(255,255,255,0.06)',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  overflow: 'hidden',
                }}
              >
                <img
                  src="/logo/logo-circle-dark-mode.svg"
                  alt="Lawis Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '8px' }}
                />
              </Box>
            </Box>
          )}

          <Container maxWidth="sm" sx={{ maxWidth: '420px !important' }}>
            {/* Section label */}
            <Typography
              sx={{
                ...stagger(0),
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.25)',
                mb: 2,
              }}
            >
              Double authentification
            </Typography>

            {/* Main heading */}
            <Typography
              sx={{
                ...stagger(1),
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 300,
                fontSize: isMobileView ? '2rem' : '2.6rem',
                color: '#fff',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                mb: 1,
              }}
            >
              Vérification
            </Typography>

            {/* Decorative line */}
            <Box
              sx={{
                ...stagger(2),
                width: '32px',
                height: '1.5px',
                backgroundColor: GOLD,
                opacity: 0.5,
                mb: 3,
                mt: 2,
                borderRadius: '1px',
              }}
            />

            {/* Subtitle with email */}
            <Typography
              sx={{
                ...stagger(3),
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.40)',
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              {loading
                ? 'Envoi du code de vérification...'
                : otpRequested
                  ? <>Entrez le code à 6 chiffres envoyé à{' '}<span style={{ color: 'rgba(255,255,255,0.70)', fontWeight: 500 }}>{email}</span></>
                  : "Veuillez patienter pendant l'envoi du code..."}
            </Typography>

            {/* Error alert */}
            {error && (
              <Fade in={!!error}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    fontFamily: '"DM Sans", sans-serif',
                    backgroundColor: 'rgba(239, 68, 68, 0.08)',
                    color: '#fca5a5',
                    border: '1px solid rgba(239, 68, 68, 0.15)',
                    '& .MuiAlert-icon': { color: '#f87171' },
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Success alert */}
            {message && (
              <Fade in={!!message}>
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    fontFamily: '"DM Sans", sans-serif',
                    backgroundColor: `rgba(201, 169, 110, 0.08)`,
                    color: GOLD,
                    border: `1px solid rgba(201, 169, 110, 0.2)`,
                    '& .MuiAlert-icon': { color: GOLD },
                  }}
                >
                  {message}
                </Alert>
              </Fade>
            )}

            {/* OTP inputs */}
            <Box
              sx={{
                ...stagger(4),
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                mb: 4,
              }}
            >
              {Array(6)
                .fill(0)
                .map((_, index) => (
                  <TextField
                    key={index}
                    inputRef={el => (inputRefs.current[index] = el)}
                    value={otp[index]}
                    onChange={e => handleOtpChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: 'center',
                        fontSize: '22px',
                        fontWeight: 600,
                        padding: '14px 0',
                        color: '#fff',
                        fontFamily: '"DM Sans", monospace',
                        letterSpacing: '0.05em',
                      },
                    }}
                    sx={{
                      flex: 1,
                      ...otpInputSx,
                    }}
                    disabled={loading}
                  />
                ))}
            </Box>

            {/* Verify button */}
            <Box sx={stagger(5)}>
              <CustomButton
                onClick={() => handleVerifyOtp()}
                text="Vérifier"
                loading={isVerifying}
                fullWidth
                disabled={otp.join('').length !== 6 || loading || isVerifying}
                sx={{
                  width: '100%',
                  py: 1.6,
                  borderRadius: '12px',
                  backgroundColor: '#fff',
                  color: '#000',
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.2s ease',
                  mb: 4,
                  ':hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.88)',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.25)',
                  },
                }}
              />
            </Box>

            {/* Resend section */}
            <Box
              sx={{
                ...stagger(6),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.25)',
                }}
              >
                Vous n'avez pas reçu de code ?
              </Typography>

              {isResendDisabled && countdown > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.25)',
                    }}
                  >
                    Renvoyer dans
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: GOLD,
                      opacity: 0.8,
                    }}
                  >
                    {formatTime(countdown)}
                  </Typography>
                </Box>
              ) : !isResendDisabled ? (
                <Box
                  component="button"
                  onClick={handleResendOtp}
                  disabled={loading}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: GOLD,
                    opacity: 0.7,
                    transition: 'opacity 0.2s ease',
                    '&:hover': { opacity: 1 },
                    '&:disabled': { opacity: 0.3, cursor: 'not-allowed' },
                  }}
                >
                  <Refresh sx={{ fontSize: '15px' }} />
                  Renvoyer le code
                </Box>
              ) : null}

              {isResendDisabled && countdown === 0 && (
                <Typography
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '13px',
                    color: 'rgba(239, 68, 68, 0.7)',
                    textAlign: 'center',
                  }}
                >
                  Nombre maximal de tentatives atteint.
                </Typography>
              )}
            </Box>

            {/* Back to login */}
            <Box sx={{ ...stagger(7), textAlign: 'center', mt: 4 }}>
              <Typography
                component="span"
                onClick={handleBackToLogin}
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.25)',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: 'rgba(255, 255, 255, 0.5)' },
                }}
              >
                ← Retour à la connexion
              </Typography>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </Box>
  )
}

export default OTPVerificationScreen
